import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideArrowLeft } from '@lucide/angular';

/**
 * App bar. Actions are projected:
 *   <ds-top-nav title="Beranda"><button ds-top-nav-action>…</button></ds-top-nav>
 *
 * Optionally show a rounded logo via [logoSrc]="'/images/logo.webp'".
 */
@Component({
  selector: 'ds-top-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <header class="bar">
      @if (showBack()) {
        <button type="button" class="bar__back" [attr.aria-label]="backLabel()" (click)="back.emit()">
          <ng-container
            [ngComponentOutlet]="backIcon"
            [ngComponentOutletInputs]="{ size: 26, strokeWidth: 2.25 }"
          />
        </button>
      }
      @if (logoSrc()) {
        <img [src]="logoSrc()" alt="Logo" class="bar__logo" />
      }
      <span class="bar__title">{{ title() }}</span>
      <div class="bar__actions">
        <ng-content select="[ds-top-nav-action]" />
      </div>
    </header>
  `,
  styles: `
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: var(--z-sticky);
    }

    .bar {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      min-height: 64px;
      padding: var(--space-2) var(--space-4);
      background: var(--color-surface);
      border-bottom: 2px solid var(--color-border-subtle);
    }

    .bar__back {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--touch-target-comfort);
      height: var(--touch-target-comfort);
      margin-left: calc(var(--space-2) * -1);
      border: 0;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-text);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover {
        background: var(--color-bg-subtle);
      }
    }

    .bar__logo {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .bar__title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: var(--font-display);
      font-size: var(--text-xl);
      font-weight: var(--font-weight-display-semibold);
      color: var(--color-text);
    }

    .bar__actions {
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }

    @media (max-width: 719px) {
      .bar {
        flex-wrap: wrap;
        gap: var(--space-2);
        padding-block: var(--space-3);
      }

      .bar__title {
        flex: 1 1 calc(100% - 52px);
        font-size: var(--text-lg);
      }

      .bar__actions {
        flex: 1 0 100%;
        justify-content: flex-end;
        min-width: 0;
        padding-left: calc(var(--touch-target-comfort) + var(--space-2));
      }

      .bar__actions > * {
        max-width: 100%;
      }
    }
  `,
})
export class TopNavComponent {
  title = input.required<string>();
  showBack = input(false);
  backLabel = input('Kembali');
  logoSrc = input<string | undefined>(undefined);
  back = output<void>();

  protected readonly backIcon = LucideArrowLeft;
}
